/**
 * The add-source form's values and the payload builders: what a platform's form collects becomes one or more
 * `IntelSourceCreate` requests (a pasted list creates one source per line).
 */
import type { IntelSourceCreate } from '../../../generated/api/types.gen.js';
import type { PlatformDef } from './platform-catalog.js';

export interface FormData {
  handle: string;
  bulkHandles: string;
  address: string;
  bulkAddresses: string;
  chain: string;
  label: string;
  tags: string[];
  protocolName: string;
  chainFilters: string[];
  entityName: string;
  ticker: string;
  entityType: string;
  sourceName: string;
  apiUrl: string;
}

export function getInitialFormData(): FormData {
  return {
    handle: '',
    bulkHandles: '',
    address: '',
    bulkAddresses: '',
    chain: 'ethereum',
    label: '',
    tags: [],
    protocolName: '',
    chainFilters: [],
    entityName: '',
    ticker: '',
    entityType: 'etf',
    sourceName: '',
    apiUrl: '',
  };
}

/** Build create payloads from form data + selected platform. Returns array for bulk support. */
export function buildCreatePayloads(platform: PlatformDef, data: FormData): IntelSourceCreate[] {
  const cat = platform.category;

  if (cat === 'social') {
    const handles: string[] = [];
    if (data.handle.trim()) handles.push(data.handle.trim());
    if (data.bulkHandles.trim()) {
      handles.push(
        ...data.bulkHandles
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean),
      );
    }
    return handles.map((h) => ({
      platform: platform.slug,
      handle: h.replace(/^@/, ''),
      category: cat,
    }));
  }

  if (cat === 'on_chain') {
    const addrs: string[] = [];
    if (data.address.trim()) addrs.push(data.address.trim());
    if (data.bulkAddresses.trim()) {
      addrs.push(
        ...data.bulkAddresses
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean),
      );
    }
    return addrs.map((addr) => ({
      platform: platform.slug,
      handle: addr,
      name: data.label || undefined,
      category: cat,
      typeMetadata: {
        type: 'on_chain',
        address: addr,
        chain: data.chain,
        tags: data.tags.length > 0 ? data.tags : undefined,
      },
    }));
  }

  if (cat === 'defi') {
    return [
      {
        platform: platform.slug,
        handle: data.protocolName.trim(),
        name: data.protocolName.trim(),
        category: cat,
        typeMetadata: {
          type: 'defi',
          protocol_name: data.protocolName.trim(),
          supported_chains: data.chainFilters.length > 0 ? data.chainFilters : undefined,
        },
      },
    ];
  }

  if (cat === 'institutional') {
    return [
      {
        platform: platform.slug,
        handle: data.ticker.trim() || data.entityName.trim().toLowerCase().replace(/\s+/g, '_'),
        name: data.entityName.trim(),
        category: cat,
        typeMetadata: {
          type: 'institutional',
          legal_name: data.entityName.trim(),
          fund_type: data.entityType,
          ticker: data.ticker.trim() || undefined,
        },
      },
    ];
  }

  if (cat === 'research' || cat === 'market_infra') {
    return [
      {
        platform: platform.slug,
        handle: data.sourceName.trim().toLowerCase().replace(/\s+/g, '_'),
        name: data.sourceName.trim(),
        category: cat,
        typeMetadata:
          cat === 'research'
            ? { type: 'research', api_endpoint: data.apiUrl.trim() || undefined }
            : { type: 'market_infra', api_endpoint: data.apiUrl.trim() || undefined },
      },
    ];
  }

  return [];
}
